import React, {FC, PropsWithChildren, useEffect} from 'react';
import {observer} from "mobx-react-lite";
import {useService} from "@yosmanyga/di";
import {UnnormalizedProps} from '@yosmanyga/core-style';
import {ThemeProvider} from '@yosmanyga/theme';
import {Button, Container, Icon, I18NText, Markdown, useIcons} from '@yosmanyga/rich-ui';
import {build as buildTheme} from './THEME';
import {IntroductionController} from "../../controller";
import {
    AuthenticationFrontendAsGuestController,
} from '@yosmanyga/authentication-frontend';

const Introduction = observer(({
    templates,
    onLogin
}: IntroductionProps) => {
    const icons = useIcons();

    const introductionController = useService(IntroductionController, []);
    const authenticationFrontendAsGuestController = useService<AuthenticationFrontendAsGuestController>(AuthenticationFrontendAsGuestController, []);
    
    useEffect(() => {
        (async () => {
            await introductionController.load();
        })();
    }, []);

    if (!introductionController.loaded) {
        return null;
    }

    return <templates.layout flow="column">
        <Markdown
        >
            {introductionController.currentText}
        </Markdown>
        <Container
            flow='row'
            align={{
                main: 'space-between',
                cross: 'center'
            }}
            margin={{
                top: 2
            }}
        >
            <Button
                onClick={() => {
                    if (introductionController.hasPrevious()) {
                        introductionController.setPrevious();
                    }
                }}
                disabled={!introductionController.hasPrevious()}
            >
                <Icon
                    component={icons.actions.back}
                />
                <I18NText>Previous</I18NText>
            </Button>
            {introductionController.hasNext()
                ? <Button
                    onClick={() => {
                        if (introductionController.hasNext()) {
                            introductionController.setNext();
                        }
                    }}
                    margin={{
                        left: 1
                    }}
                >
                    <I18NText>Next</I18NText>
                    <Icon
                        component={icons.actions.go}
                    />
                </Button>
                : <Button
                    margin={{
                        left: 1
                    }}
                    progress={authenticationFrontendAsGuestController.completing}
                    onClick={async () => {
                        await authenticationFrontendAsGuestController.complete();

                        onLogin();
                    }}
                >
                    <I18NText>Log in as anonymous</I18NText>
                    <Icon
                        component={icons.actions.play}
                    />
                </Button>
            }
        </Container>
    </templates.layout>
});

type IntroductionProps = {
    templates: {
        layout: FC<PropsWithChildren & UnnormalizedProps>;
    },
    onLogin: () => void;
};

const IntroductionWithTheme = (props: IntroductionProps) => {
    return <ThemeProvider build={buildTheme}>
        <Introduction {...props} />
    </ThemeProvider>
};

export {IntroductionWithTheme as Introduction};
